/**
 * The club's shared UI vocabulary (glossary: docs/glossary.md, UX review T7).
 * One label per concept for every app — web, referee PWA, iOS app, boards.
 * Plain TypeScript, no JSON, no dependencies, so the React Native and Vite
 * apps can import `@flm9/i18n/labels` without pulling in the rest of the
 * package. The watch app (Swift) mirrors the few labels it needs by hand.
 */

/** Discipline codes (`disciplines.code`). */
export const DISCIPLINE_LABEL: Record<string, string> = {
  pistol: 'Pistole',
  pistol_carbine: 'Pistolenkarabiner',
  rifle: 'Gewehr',
};

export function disciplineLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  return DISCIPLINE_LABEL[code] ?? code;
}

/** Training (range session) status — never show the raw enum value. */
export type TrainingStatus = 'draft' | 'scheduled' | 'live' | 'closed' | 'locked' | 'cancelled';

export const TRAINING_STATUS_LABEL: Record<TrainingStatus, string> = {
  draft: 'Entwurf',
  scheduled: 'Geplant',
  live: 'Läuft',
  closed: 'Beendet',
  locked: 'Gesperrt',
  cancelled: 'Abgesagt',
};

/** Badge tone per training status (same colours in every app). */
export const TRAINING_STATUS_TONE: Record<
  TrainingStatus,
  'neutral' | 'success' | 'info' | 'warning' | 'danger' | 'violet'
> = {
  draft: 'neutral',
  scheduled: 'violet',
  live: 'success',
  closed: 'info',
  locked: 'warning',
  cancelled: 'danger',
};

/**
 * Not started yet: a real draft (`draft`) or an announced training whose
 * invitations went out (`scheduled`). Both are still fully editable and can
 * be started / cancelled; only `scheduled` shows up in the calendar.
 */
export function isTrainingNotStarted<S extends string>(
  status: S,
): status is S & ('draft' | 'scheduled') {
  return status === 'draft' || status === 'scheduled';
}

export function trainingStatusLabel(status: string): string {
  return TRAINING_STATUS_LABEL[status as TrainingStatus] ?? status;
}

/** User roles (`role` enum). */
export const ROLE_LABEL: Record<'admin' | 'range_officer' | 'member' | 'spectator', string> = {
  admin: 'Admin',
  range_officer: 'Standführer:in',
  member: 'Mitglied',
  spectator: 'Zuschauer:in',
};

/** Invitation answer (`invitation_vote`) + the admin-only "already enrolled" state. */
export const VOTE_LABEL: Record<'pending' | 'yes' | 'no' | 'enrolled', string> = {
  pending: 'Ausstehend',
  yes: 'Zugesagt',
  no: 'Abgesagt',
  enrolled: 'Angemeldet',
};

/** Badge tone per answer: a "no" is an answer, not an error. */
export const VOTE_TONE: Record<
  'pending' | 'yes' | 'no' | 'enrolled',
  'neutral' | 'success' | 'warning'
> = {
  pending: 'warning',
  yes: 'success',
  no: 'neutral',
  enrolled: 'success',
};

/**
 * Payment status of a membership due / participation fee (`payment_status`).
 * One map for web + app (Mitgliedschaft, Rechnungen, payment cards); tone in
 * `PAYMENT_STATUS_TONE`.
 */
export const PAYMENT_STATUS_LABEL: Record<'pending' | 'paid' | 'exempt' | 'void', string> = {
  pending: 'Offen',
  paid: 'Bezahlt',
  exempt: 'Befreit',
  void: 'Storniert',
};

/** Member document kinds (`member_document_kind`). */
export const DOCUMENT_KIND_LABEL: Record<string, string> = {
  wbk: 'Waffenbesitzkarte (WBK)',
  waffenpass: 'Waffenpass',
  jagdkarte: 'Jagdkarte',
  waffenfuehrerschein: 'Waffenführerschein',
  generic: 'Sonstiges Dokument',
};

/** Club calendar event categories (`club_event_category`). */
export const CLUB_EVENT_CATEGORY_LABEL: Record<string, string> = {
  general_meeting: 'Versammlung',
  social: 'Soziales',
  competition: 'Bewerb',
  course: 'Kurs',
  maintenance: 'Wartung',
  other: 'Sonstiges',
};

/** Scoring mode of a stage. */
export const MODE_LABEL: Record<'precision' | 'speed', string> = {
  precision: 'Präzision',
  speed: 'Speed',
};

/**
 * Shot timer states — one meaning everywhere: "Bereit" = armed, waiting for
 * the start signal (never "idle").
 */
export const TIMER_STATE_LABEL: Record<'idle' | 'armed' | 'running' | 'stopped', string> = {
  idle: 'Wartet',
  armed: 'Bereit – warte auf Startsignal',
  running: 'Läuft',
  stopped: 'Gestoppt',
};

/**
 * Payment status badge tone, one map for web + app (UX review 2026-10-30).
 * `exempt` is its own state ("Befreit"), not the payment method "Frei".
 */
export const PAYMENT_STATUS_TONE: Record<
  'pending' | 'paid' | 'exempt' | 'void',
  'warning' | 'success' | 'info' | 'neutral'
> = {
  pending: 'warning',
  paid: 'success',
  exempt: 'info',
  void: 'neutral',
};

/**
 * Accounting document status as shown to people. Only invoices are ever
 * settled (`paid`); receipts and Storno documents stay `issued` and read
 * "Ausgestellt", never "offen".
 */
export function documentStatusLabel(type: string, status: string): string {
  if (status === 'cancelled') return 'Storniert';
  if (status === 'draft') return 'Entwurf';
  if (type !== 'invoice') return 'Ausgestellt';
  return status === 'paid' ? 'Bezahlt' : 'Offen';
}

/** Who confirmed a member's own training / scenario attempt. */
export const SIGN_OFF_LABEL: Record<'self' | 'officer', string> = {
  self: 'Selbst-signiert',
  officer: 'Standführer-bestätigt',
};

/** Referee penalty types (DB enum `penalty_code`). IPSC terms stay English
 *  (glossary, user decision 2026-10-30): Procedural, Miss, No-Shoot, Extra Shot. */
export type PenaltyCode = 'procedural' | 'miss' | 'no_shoot' | 'extra_shot';

export const PENALTY_CODE_LABEL: Record<PenaltyCode, string> = {
  procedural: 'Procedural',
  miss: 'Miss',
  no_shoot: 'No-Shoot',
  extra_shot: 'Extra Shot',
};

export function penaltyCodeLabel(code: string): string {
  return PENALTY_CODE_LABEL[code as PenaltyCode] ?? code;
}

/** Stage status (`stage_status`). `active` = opened for scoring (live or
 *  already shot); one word in web + app. */
export const STAGE_STATUS_LABEL: Record<'planned' | 'active' | 'skipped', string> = {
  planned: 'Geplant',
  active: 'Gewertet',
  skipped: 'Übersprungen',
};

/** Decimal comma without Intl (Hermes/tvOS-safe): 5.1234 → "5,123". */
function decimalComma(value: number, digits: number): string {
  return value.toFixed(digits).replace('.', ',');
}

/** Hit Factor, always 3 decimals with a comma (glossary: "5,123"). */
export function formatHitFactor(hf: number | null | undefined): string {
  return hf == null || !Number.isFinite(hf) ? '–' : decimalComma(hf, 3);
}

/** Seconds with a comma and a space before the unit: 12.3456 → "12,35 s". */
export function formatSeconds(seconds: number | null | undefined, digits = 2): string {
  return seconds == null || !Number.isFinite(seconds) ? '–' : `${decimalComma(seconds, digits)} s`;
}

/** Milliseconds as seconds: 12345 → "12,35 s". */
export function formatMs(ms: number | null | undefined, digits = 2): string {
  return ms == null ? '–' : formatSeconds(ms / 1000, digits);
}

/** Date as dd.MM.yyyy without Intl. Date-only strings ("YYYY-MM-DD") are
 *  taken as calendar dates (no UTC shift); anything else in local time. */
export function formatDateDE(value: string | Date | null | undefined): string {
  if (value == null) return '–';
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  }
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '–';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

/**
 * Gendered person terms (club setting `locale.gender_style`, Einstellungen ›
 * Sprache). `neutral` = the colon form used so far ("Standführer:in"),
 * `masculine` = classic generic masculine ("Standführer"). Every UI text,
 * template and PDF that names a person role must use `term()` instead of a
 * hard-coded form. Articles: nominative only (`withArticle`); other cases are
 * worded per style at the call site.
 */
export type GenderStyle = 'neutral' | 'masculine';

export const GENDER_STYLE_LABEL: Record<GenderStyle, string> = {
  neutral: 'Neutral (Standführer:in)',
  masculine: 'Klassisch (Standführer)',
};

interface TermForms {
  sg: string;
  pl: string;
  /** Nominative articles for the singular: definite / indefinite. */
  def: string;
  indef: string;
}

export const TERMS = {
  rangeOfficer: {
    neutral: { sg: 'Standführer:in', pl: 'Standführer:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Standführer', pl: 'Standführer', def: 'der', indef: 'ein' },
  },
  shooter: {
    neutral: { sg: 'Schütz:in', pl: 'Schütz:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Schütze', pl: 'Schützen', def: 'der', indef: 'ein' },
  },
  participant: {
    neutral: { sg: 'Teilnehmer:in', pl: 'Teilnehmer:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Teilnehmer', pl: 'Teilnehmer', def: 'der', indef: 'ein' },
  },
  recipient: {
    neutral: { sg: 'Empfänger:in', pl: 'Empfänger:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Empfänger', pl: 'Empfänger', def: 'der', indef: 'ein' },
  },
  user: {
    neutral: { sg: 'Benutzer:in', pl: 'Benutzer:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Benutzer', pl: 'Benutzer', def: 'der', indef: 'ein' },
  },
  spectator: {
    neutral: { sg: 'Zuschauer:in', pl: 'Zuschauer:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Zuschauer', pl: 'Zuschauer', def: 'der', indef: 'ein' },
  },
  payer: {
    neutral: { sg: 'Zahler:in', pl: 'Zahler:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Zahler', pl: 'Zahler', def: 'der', indef: 'ein' },
  },
  signer: {
    neutral: { sg: 'Unterzeichner:in', pl: 'Unterzeichner:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Unterzeichner', pl: 'Unterzeichner', def: 'der', indef: 'ein' },
  },
  supplier: {
    neutral: { sg: 'Lieferant:in', pl: 'Lieferant:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Lieferant', pl: 'Lieferanten', def: 'der', indef: 'ein' },
  },
  author: {
    neutral: { sg: 'Autor:in', pl: 'Autor:innen', def: 'die', indef: 'eine' },
    masculine: { sg: 'Autor', pl: 'Autoren', def: 'der', indef: 'ein' },
  },
} as const satisfies Record<string, Record<GenderStyle, TermForms>>;

export type TermKey = keyof typeof TERMS;

/** "Standführer:in" / "Standführer" (plural: "Standführer:innen" / "Standführer"). */
export function term(
  key: TermKey,
  style: GenderStyle = 'neutral',
  opts: { plural?: boolean } = {},
): string {
  const forms = TERMS[key][style] ?? TERMS[key].neutral;
  return opts.plural ? forms.pl : forms.sg;
}

/** Singular with nominative article: "die Standführer:in" / "ein Standführer". */
export function withArticle(
  key: TermKey,
  style: GenderStyle = 'neutral',
  article: 'def' | 'indef' = 'def',
): string {
  const forms = TERMS[key][style] ?? TERMS[key].neutral;
  return `${forms[article]} ${forms.sg}`;
}

/** Role label following the gender style ("Standführer:in" / "Standführer"). */
export function roleLabel(
  role: 'admin' | 'range_officer' | 'member' | 'spectator',
  style: GenderStyle = 'neutral',
): string {
  if (role === 'range_officer') return term('rangeOfficer', style);
  if (role === 'spectator') return term('spectator', style);
  return ROLE_LABEL[role];
}
